/**
 * Smart Card value source: /tbl/sc-vds with optional /user/fo-detail fallback.
 *
 * SC_VDS_FALLBACK_TO_FO_DETAIL controls how sc-vds values combine with the
 * base smart cards that come from /user/fo-detail:
 *   true  (fallback ON)  — keep the fo-detail (Tableau) values; sc-vds overrides
 *                          a card only when it supplies a value for that scId.
 *                          Cards are always populated.
 *   false (fallback OFF) — suppress the fo-detail values entirely; only sc-vds
 *                          populates them. A card that shows values therefore
 *                          proves the data came from sc-vds (else it renders N/A).
 *
 * AGENT-CONTEXT: @invariant Single toggle for the smart-card value source.
 * Flip this one flag to switch between sc-vds-only and fo-detail fallback.
 * Override only if the task explicitly changes the smart-card value source.
 */
export const SC_VDS_FALLBACK_TO_FO_DETAIL = false;

/**
 * Reduce a /tbl/sc-vds response array to a { [scId]: {value fields} } lookup.
 * Besides the 5 display values this carries the VDS attention score and the
 * "Needs Attention" rank (rankTrend) derived from it; fo-detail no longer
 * supplies a rank, so sorting comes from sc-vds (and its cache) only.
 */
export const toSmartCardVdsByScId = (vdsSmarts = []) => {
  const byScId = {};
  (Array.isArray(vdsSmarts) ? vdsSmarts : []).forEach((card) => {
    if (card?.scId == null) return;
    byScId[card.scId] = {
      pmpm: card.pmpm ?? null,
      estimatedSavings: card.estimatedSavings ?? null,
      reachImpactPct: card.reachImpactPct ?? null,
      costTrend: card.costTrend ?? null,
      quartile: card.quartile ?? null,
      attentionScore: card.attentionScore ?? null,
      rankTrend: card.rankTrend ?? null,
    };
  });
  return byScId;
};

/**
 * Overlay sc-vds values onto base smart cards, matched by scId.
 *
 * @param {Array} smarts base smart cards (from fo-detail)
 * @param {Object} byScId sc-vds values keyed by scId
 * @param {boolean} fallback when true, cards without a sc-vds value keep their
 *   base values; when false, those cards have their value fields cleared
 */
export const applySmartCardVdsOverlay = (
  smarts,
  byScId = {},
  fallback = SC_VDS_FALLBACK_TO_FO_DETAIL
) => {
  if (!Array.isArray(smarts)) return smarts;
  const hasVds = byScId && Object.keys(byScId).length > 0;
  // Fallback on with no sc-vds values yet: leave the base cards untouched.
  if (fallback && !hasVds) return smarts;

  return smarts.map((card) => {
    const values = card?.scId != null ? byScId[card.scId] : undefined;
    if (values) return { ...card, ...values };
    if (fallback) return card;
    return {
      ...card,
      pmpm: null,
      estimatedSavings: null,
      reachImpactPct: null,
      costTrend: null,
      quartile: null,
      attentionScore: null,
      rankTrend: null,
    };
  });
};
